// Server-owned queue; every response belongs to one local generation.
import { api } from './api.js';
import { go, register, choice, $ } from './ui.js';
import { mmss } from './format.js';

let owner = null, screen = null, tick = null;
const leaveDialog = $('queue-leave-dialog');
const waitingScreen = role => role === 'listener' ? 'listener-waiting' : 'talker-matching';
const isOwner = value => owner === value;

export function stopQueue() {
  const old = owner; owner = null;
  clearTimeout(old?.timer); clearInterval(tick); tick = null;
  leaveDialog.close();
}
function body(o) { return { entryId: o.entryId, connectionId: o.connectionId }; }
function show(o) {
  if (!isOwner(o) || screen !== waitingScreen(o.role)) return;
  const s = o.state, listener = o.role === 'listener', prefix = listener ? 'listener' : 'talker';
  const now = Date.now() + (o.offset || 0);
  let text = o.error || 'connecting…';
  if (!o.error && s) {
    if (o.retiring) text = 'finishing the previous connection…';
    else if (s.status === 'waiting') text = listener ? 'waiting for a talker…' : 'waiting for a listener…';
    else if (s.status === 'offered') text = listener ? 'a talker is ready. start within two minutes.' : 'waiting for your listener to join…';
    else if (['closing', 'starting'].includes(s.status)) text = s.status === 'closing' ? 'finding the next available listener…' : 'preparing your call…';
    else if (s.status === 'paused') text = listener ? 'your availability is paused.' : 'your search is paused. reconnecting…';
    else if (s.status === 'expired') text = 'no listener became available in time.';
    else if (s.status === 'idle') text = 'you left the queue.';
  }
  $(prefix + '-status').textContent = text;
  const deadline = s?.offer?.expiresAt ?? (!listener && s?.status === 'waiting' ? s.searchUntil : null);
  $(prefix + '-countdown').textContent = deadline ? mmss(Math.max(0, deadline - now)) + ' left' : '';
  $(prefix + '-countdown').hidden = !deadline;
  $(prefix + '-cancel').disabled = !!o.busy;
  if (listener) {
    $('listener-accept').hidden = s?.status !== 'offered' || !!o.retiring;
    $('listener-accept').disabled = !!o.busy;
    $('listener-return').hidden = !['paused', 'idle'].includes(s?.status);
  } else {
    $('talker-restart').hidden = !['expired', 'idle'].includes(s?.status);
  }
}
function consume(o, state) {
  if (!isOwner(o)) return;
  if (o.state && state.revision < o.state.revision) return;
  o.state = state; o.offset = state.serverNow - Date.now(); o.error = '';
  // Preserve the user's leave decision while a poll/match or cancel races it.
  // Staying resumes the latest state; confirmed cancellation is server-owned.
  if (o.confirming || o.cancelling) { show(o); return; }
  if (o.retiring && state.offer?.sessionId === o.retiring) { show(o); return; }
  o.retiring = null;
  if (state.offer?.sessionId && ['joining', 'active'].includes(state.offer.status)) {
    if (o.opened !== state.offer.sessionId) {
      o.opened = state.offer.sessionId;
      go('permission', { sessionId: o.opened });
    }
    if (state.status === 'active') { clearTimeout(o.timer); o.finished = true; }
  } else if (o.opened) {
    o.opened = null;
    go(waitingScreen(o.role), { owner: o });
  }
  show(o);
}
async function request(o, action, extra = {}) {
  const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 12000);
  try {
    const result = await api('/api/queue/' + action, { method: 'POST', body: { ...body(o), ...extra }, signal: controller.signal });
    consume(o, result);
  } finally { clearTimeout(timer); }
}
async function poll(o, resume = false) {
  if (!isOwner(o) || o.finished) return;
  try { await request(o, 'poll', { resume }); }
  catch (e) {
    if (!isOwner(o)) return;
    if (e.status === 401) { stopQueue(); go('gate'); return; }
    if (e.data?.error === 'replaced') {
      o.finished = true; o.error = 'your queue is open in another tab. return to the home screen to continue.';
    } else o.error = 'connection lost. trying again…';
    show(o);
  }
  if (isOwner(o) && !o.finished) o.timer = setTimeout(() => poll(o), 3000);
}
function install(role, entryId, pref) {
  stopQueue();
  const o = { role, pref, entryId, connectionId: crypto.randomUUID(), state: null };
  owner = o;
  tick = setInterval(() => show(o), 500);
  go(waitingScreen(role), { owner: o });
  return o;
}
export function resumeQueue(state, retiring = null) {
  if (!state?.entryId || state.status === 'idle') return false;
  const o = install(state.role, state.entryId, state.pref);
  o.retiring = retiring;
  consume(o, state);
  void poll(o, true);
  return true;
}
export async function recoverQueue(sessionId) {
  try {
    const state = await api('/api/queue');
    if (!resumeQueue(state, sessionId)) { stopQueue(); go('role'); }
  } catch { stopQueue(); go('role'); }
}
async function start(role, pref) {
  const o = install(role, crypto.randomUUID(), pref);
  try {
    await request(o, 'enqueue', { role, pref });
    if (isOwner(o)) void poll(o);
  } catch (e) {
    if (!isOwner(o)) return;
    if (e.status === 401) { stopQueue(); go('gate'); return; }
    if (e.data?.error === 'already_busy') return recoverQueue();
    o.error = 'couldn’t join the queue. leave the queue and try again.'; show(o);
  }
}
async function cancel() {
  const o = owner;
  if (!o || o.busy) return;
  o.confirming = false; o.cancelling = true; o.busy = true; leaveDialog.close(); show(o);
  try {
    await request(o, 'cancel');
    if (isOwner(o)) { stopQueue(); go('queue-left'); }
  } catch (e) {
    if (!isOwner(o)) return;
    if (e.data?.error === 'replaced') { stopQueue(); go('role'); return; }
    o.error = 'couldn’t leave the queue. try again.';
  } finally {
    o.cancelling = false; o.busy = false;
    if (isOwner(o) && o.state?.offer?.sessionId) consume(o, o.state); else show(o);
  }
}
function confirmLeave() {
  if (!owner || owner.busy) return;
  owner.confirming = true; leaveDialog.showModal();
}
function stay() {
  leaveDialog.close();
  if (owner) { owner.confirming = false; if (owner.state) consume(owner, owner.state); }
}
$('queue-stay').addEventListener('click', stay);
$('queue-leave').addEventListener('click', cancel);
leaveDialog.addEventListener('cancel', event => { event.preventDefault(); stay(); });
$('listener-start').addEventListener('click', () => start('listener', choice('listener-pref')));
$('talker-start').addEventListener('click', () => start('talker', choice('talker-pref')));
$('listener-return').addEventListener('click', () => { if (owner && !owner.busy) start('listener', owner.pref); });
$('talker-restart').addEventListener('click', () => { if (owner && !owner.busy) start('talker', owner.pref); });
$('listener-cancel').addEventListener('click', confirmLeave);
$('talker-cancel').addEventListener('click', confirmLeave);
$('listener-accept').addEventListener('click', async () => {
  const o = owner;
  if (!o || o.busy || o.state?.status !== 'offered') return;
  o.busy = true; show(o);
  try { await request(o, 'accept', { offerId: o.state.offer.id }); }
  catch { if (isOwner(o)) { o.error = 'couldn’t start. trying again…'; show(o); } }
  finally { o.busy = false; show(o); }
});
for (const name of ['listener-waiting', 'talker-matching']) register({
  [name]: { enter() { screen = name; if (owner) show(owner); }, leave() { screen = null; } }
});
let leftTimer;
register({ 'queue-left': {
  enter() { leftTimer = setTimeout(() => go('role'), 3000); },
  leave() { clearTimeout(leftTimer); }
} });
